import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "./use-user-role";

export type ViewerRole = "admin" | "coach" | "parent" | "guest";

interface ViewerRoleResult {
  role: ViewerRole;
  displayName: string | null;
  loading: boolean;
}

const SESSION_TIMEOUT_MS = 2_000;

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T | null> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timeoutId = setTimeout(() => resolve(null), timeoutMs);
  });

  return Promise.race([promise, timeout]).finally(() => {
    if (timeoutId) clearTimeout(timeoutId);
  });
}

/**
 * Lightweight viewer classification used by the sign-in state accent bar.
 *
 * Cheap on purpose:
 * - relies on the auth session + existing `useUserRole` roles
 * - does NOT run an extra RPC — a signed-in user with no admin/coach role
 *   is treated as a parent (accurate for the vast majority of viewers, and
 *   the bar is a subtle cue, not a permissions check).
 *
 * Priority: admin > coach > parent > guest.
 */
export function useViewerRole(): ViewerRoleResult {
  const { isAdmin, isCoach, loading: rolesLoading } = useUserRole();
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const read = async () => {
      const result = await withTimeout(supabase.auth.getSession(), SESSION_TIMEOUT_MS);
      const session = result?.data?.session ?? null;
      if (cancelled) return;
      if (!session) {
        setSignedIn(false);
        setDisplayName(null);
        return;
      }
      setSignedIn(true);
      const meta = (session.user.user_metadata ?? {}) as Record<string, unknown>;
      const name =
        (typeof meta.name === "string" && meta.name) ||
        (typeof meta.full_name === "string" && meta.full_name) ||
        session.user.email ||
        null;
      setDisplayName(typeof name === "string" ? name.split(" ")[0] : null);
    };
    read();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => read());
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  let role: ViewerRole = "guest";
  if (signedIn) {
    if (isAdmin) role = "admin";
    else if (isCoach) role = "coach";
    else role = "parent";
  }

  return {
    role,
    displayName,
    loading: signedIn === null || rolesLoading,
  };
}
